import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { rackHeightM, rackWidthM } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { LayoutCell, LayoutRack } from '../../warehouse/api/warehouse.api.js';
import { cellStatusLabel } from '../../warehouse/model/cell-status.js';
import { cellCodeMeaning } from '../../warehouse/ui/CellCode.js';
import {
  CELL_SHADOW,
  beamStyle,
  byLevel,
  cm,
  rackLabel,
  shortCode,
  uprightStyle,
} from '../../warehouse/ui/RackFacade.js';

/**
 * «QAYERDA TURIBDI» CHIZMASI — faqat o'qish va topish uchun.
 *
 * Nega `RackFacade` emas: u ombor sahifasining ISH chizmasi (etiketka
 * tanlash, qatorni o'lchash, savat) va besh joyda ishlatiladi. U yerda
 * kataklar javonning haqiqiy nisbatida SIQILADI — 13 o'rinli javon
 * telefonda 25px li kataklarga bo'linib, matni yashirinib qolardi.
 * Bu yerda savol bitta — «mahsulot qaysi katakda?» — va javob O'QILISHI
 * shart. Shuning uchun:
 *
 *   • katakning eng kichik eni bor (`--cell-min`) va chizma o'z
 *     qutisida yon tomonga aylanadi; sahifa esa aylanmaydi;
 *   • ochilganda tanlangan katak qutining MARKAZIGA suriladi;
 *   • aylantirish mumkin bo'lgan tomonda yengil gradient turadi;
 *   • qator raqami chap chetga yopishadi — aylantirilganda ham ko'rinadi;
 *   • o'lchamlar chizmaning ICHIDA emas, ostida bitta matn qatori.
 */

export interface RackLocatorProps {
  rack: LayoutRack;
  /** Mahsulot turgan kataklar: `id` → tayyor matn (`56 dona`). */
  marked: ReadonlyMap<string, string>;
  selectedCellId: string | null;
  onSelectCell: (cellId: string) => void;
}

/** Qator raqami ustunining eni — yopishqoq, ixcham. */
const RULER_W = '2.75rem';
const POST_W = '9px';

const cellLabel = (cell: LayoutCell, here: string | undefined): string =>
  [
    cell.code,
    cellCodeMeaning(cell.code) ?? '',
    cellStatusLabel(cell.status),
    here ?? '',
    cell.topItem ?? '',
    ...cell.reservedFor.map((item) => t('warehouse.cellReservedBadge', { name: item.name })),
  ]
    .filter((part) => part !== '')
    .join(' · ');

export const RackLocator = ({ rack, marked, selectedCellId, onSelectCell }: RackLocatorProps) => {
  const reduceMotion = useReducedMotion() === true;
  const levels = rack.levelHeights.length;
  const rows = useMemo(() => byLevel(rack.cells, levels), [rack.cells, levels]);
  const widest = Math.max(1, ...rows.map((row) => row.length));

  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback((): void => {
    const node = scroller.current;
    if (node === null) return;
    const left = node.scrollLeft > 1;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setEdges((current) =>
      current.left === left && current.right === right ? current : { left, right },
    );
  }, []);

  useEffect(() => {
    measure();
    const node = scroller.current;
    if (node === null || typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, rack.id]);

  /*
   * TANLANGAN KATAK MARKAZGA.
   *
   * `scrollIntoView({ inline: 'center' })` bilan bir xil natija, lekin
   * qo'lda: markaz yopishqoq raqam ustunisiz hisoblanadi — aks holda
   * katak o'sha ustun ortiga qisman kirib qolardi. Vertikal esa
   * «nearest» — faqat katak ko'rinmay turgan bo'lsa.
   */
  useEffect(() => {
    const node = scroller.current;
    if (node === null || selectedCellId === null) return;
    const target = Array.from(node.querySelectorAll<HTMLElement>('[data-cell-id]')).find(
      (el) => el.dataset.cellId === selectedCellId,
    );
    if (target === undefined || typeof node.scrollBy !== 'function') return;

    const box = node.getBoundingClientRect();
    const cell = target.getBoundingClientRect();
    const ruler = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) * 2.75;
    const center = box.left + ruler + (box.width - ruler) / 2;
    const behavior = reduceMotion ? 'auto' : 'smooth';
    const delta = cell.left + cell.width / 2 - center;
    if (Math.abs(delta) >= 2) node.scrollBy({ left: delta, behavior });

    /* VERTIKAL — «nearest»: katak oyna tanasida ko'rinmasa (masalan pastki
       qavatda), tana uni ko'rinadigan qilib eng kam suriladi. Ko'rinib
       turgan bo'lsa tegilmaydi va tepadagi ro'yxat joyida qoladi. */
    const body = node.parentElement?.closest<HTMLElement>('.overflow-y-auto');
    if (body === null || body === undefined) return;
    const view = body.getBoundingClientRect();
    const pad = 12;
    const below = cell.bottom + pad - view.bottom;
    const above = view.top - (cell.top - pad);
    if (below > 0) body.scrollBy({ top: Math.min(below, cell.top - pad - view.top), behavior });
    else if (above > 0) body.scrollBy({ top: -above, behavior });
  }, [selectedCellId, rack.id, reduceMotion]);

  const style = {
    '--locator-ruler': RULER_W,
    '--locator-post': POST_W,
    /* Chizma eni — eng ko'p o'rinli qator bo'yicha. Kichik bo'lsa quti
       eniga cho'ziladi, katta bo'lsa yon tomonga aylanadi. */
    minWidth: `calc(var(--locator-ruler) + 2 * var(--locator-post) + ${String(widest)} * (var(--cell-min) + 2px) + 2px)`,
  } as CSSProperties;

  return (
    <section aria-label={rackLabel(rack)} className="flex min-w-0 flex-col gap-2">
      <h3 className="text-base font-semibold text-text">{rackLabel(rack)}</h3>

      <div className="relative min-w-0">
        <div
          ref={scroller}
          onScroll={measure}
          /* Klaviatura bilan ham aylantirish mumkin bo'lsin — faqat
             haqiqatan aylanadigan bo'lsa fokus to'xtaydi. */
          tabIndex={edges.left || edges.right ? 0 : -1}
          role="group"
          aria-label={edges.left || edges.right ? t('stock.locationScrollHint') : undefined}
          className={cn(
            'min-w-0 overflow-x-auto overscroll-x-contain rounded-[var(--radius-md)]',
            'border border-border bg-surface',
            /* Ingichka aylantirish chizig'i — chizmaning O'Z qutisida,
               o'lcham yozuvidan alohida. */
            '[scrollbar-width:thin] [scrollbar-color:var(--color-border-strong)_transparent]',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            /* Telefonda katak kengroq — o'qish nisbatdan muhim. */
            '[--cell-min:7.25rem] sm:[--cell-min:7.5rem] lg:[--cell-min:5.5rem]',
          )}
        >
          <div className="flex flex-col" style={style}>
            {rows.map((row, rowIndex) => {
              const level = levels - rowIndex;
              const heightCm = cm(rack.levelHeights[level - 1] ?? 0);
              return (
                <div
                  key={level}
                  className="flex min-h-[5.75rem]"
                  /* Balandlik nisbati SAQLANADI, lekin mazmundan kichik
                     bo'lmaydi: `flexBasis: auto` — avval matn joyi, ortib
                     qolgani qator balandligi nisbatida. */
                  style={{ flexGrow: rack.levelHeights[level - 1] ?? 1, flexBasis: 'auto' }}
                >
                  {/* QATOR RAQAMI — yopishqoq, chizma aylansa ham turadi. */}
                  <div
                    className={cn(
                      'sticky left-0 z-20 flex w-[var(--locator-ruler)] shrink-0 flex-col items-center justify-center gap-1',
                      'border-r border-border bg-surface',
                      edges.left ? 'shadow-[4px_0_8px_-4px_rgb(0_0_0/0.18)]' : undefined,
                    )}
                  >
                    <span
                      aria-hidden
                      className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] bg-primary text-sm font-bold text-primary-contrast tabular-nums"
                    >
                      {level}
                    </span>
                    <span className="text-[0.6875rem] leading-none text-text-muted tabular-nums">
                      <span className="sr-only">{t('warehouse.levelLabel', { level })} · </span>
                      {heightCm}
                    </span>
                  </div>

                  <div
                    className="w-[var(--locator-post)] shrink-0"
                    style={uprightStyle}
                    aria-hidden
                  />

                  <div className="flex min-w-0 flex-1 flex-col bg-surface-2">
                    <div className="flex flex-1 gap-[2px] p-[2px]">
                      {row.map((cell) => (
                        <LocatorCell
                          key={cell.id}
                          cell={cell}
                          level={level}
                          bay={rack.bays[level - 1]?.[cell.idx - 1] ?? 1}
                          heightCm={heightCm}
                          here={marked.get(cell.id)}
                          selected={cell.id === selectedCellId}
                          reduceMotion={reduceMotion}
                          onSelect={onSelectCell}
                        />
                      ))}
                    </div>
                    <div className="h-[4px] shrink-0" style={beamStyle} aria-hidden />
                  </div>

                  <div
                    className="w-[var(--locator-post)] shrink-0"
                    style={uprightStyle}
                    aria-hidden
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Aylantirish mumkinligi — yengil soya. Chap soya yopishqoq
            raqam ustunidan KEYIN boshlanadi. */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-px left-[calc(var(--locator-ruler,2.75rem)+1px)] z-30 w-6',
            'bg-gradient-to-r from-[color-mix(in_oklab,var(--color-text)_14%,transparent)] to-transparent',
            'transition-opacity duration-[var(--motion-fast)]',
            edges.left ? 'opacity-100' : 'opacity-0',
          )}
          style={{ '--locator-ruler': RULER_W } as CSSProperties}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-px right-px z-30 w-8 rounded-r-[var(--radius-md)]',
            'bg-gradient-to-l from-[color-mix(in_oklab,var(--color-text)_14%,transparent)] to-transparent',
            'transition-opacity duration-[var(--motion-fast)]',
            edges.right ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>

      {/* O'LCHAMLAR — chizmaning ostida, alohida qator. */}
      <p className="text-xs text-text-muted tabular-nums">
        {t('stock.locationSize', {
          width: cm(rackWidthM(rack)),
          depth: cm(rack.depthM),
          height: cm(rackHeightM(rack)),
        })}
      </p>
    </section>
  );
};

interface LocatorCellProps {
  cell: LayoutCell;
  level: number;
  /** Proyom eni (m) — katak eni shu nisbatda. */
  bay: number;
  heightCm: string;
  here: string | undefined;
  selected: boolean;
  reduceMotion: boolean;
  onSelect: (cellId: string) => void;
}

/**
 * Bitta katak. Mahsulot TURGAN katak — tugma (bosilsa tanlanadi),
 * qolganlari oddiy blok: ularni bosish hech narsa qilmaydi va `Tab`
 * ularda to'xtamasligi kerak.
 */
const LocatorCell = ({
  cell,
  level,
  bay,
  heightCm,
  here,
  selected,
  reduceMotion,
  onSelect,
}: LocatorCellProps) => {
  const found = here !== undefined;
  const strong = found && selected;

  const content = (
    <>
      <span
        className={cn(
          'font-mono text-xs font-semibold tracking-wide',
          strong ? 'text-accent-contrast' : 'text-text',
        )}
      >
        {shortCode(level, cell.idx)}
      </span>

      {found ? (
        <span
          className={cn(
            'text-sm font-bold tabular-nums',
            strong ? 'text-accent-contrast' : 'text-accent',
          )}
        >
          {here}
        </span>
      ) : null}

      {cell.topItem === null ? null : (
        <span
          className={cn(
            'line-clamp-2 w-full text-xs leading-snug break-words',
            strong ? 'text-accent-contrast' : found ? 'text-text' : 'text-text-muted',
          )}
        >
          {cell.topItem}
        </span>
      )}

      <span
        className={cn(
          'text-[0.6875rem] tabular-nums',
          strong ? 'text-accent-contrast' : 'text-text-muted',
        )}
      >
        {cell.fillPercent > 0 ? `${String(cell.fillPercent)}% · ` : ''}
        {cm(bay)}×{heightCm}
      </span>
    </>
  );

  const shell = cn(
    'relative flex min-h-11 w-full flex-1 flex-col items-center justify-center gap-0.5',
    'rounded-[4px] px-1.5 py-1.5 text-center',
  );

  return (
    <div
      className="relative flex"
      style={{ flexGrow: bay, flexBasis: 0, minWidth: 'var(--cell-min)' }}
      data-cell-id={cell.id}
    >
      {found ? (
        <button
          type="button"
          aria-pressed={selected}
          aria-label={cellLabel(cell, here)}
          onClick={() => onSelect(cell.id)}
          className={cn(
            shell,
            'transition-[background-color,box-shadow,transform] duration-[var(--motion-fast)]',
            'active:scale-[0.98] motion-reduce:active:scale-100',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
            strong
              ? 'z-10 bg-accent shadow-[var(--shadow-2)] ring-2 ring-accent ring-offset-2 ring-offset-surface-2'
              : 'border-2 border-accent bg-accent-soft hover:bg-surface',
          )}
        >
          {strong && !reduceMotion ? (
            <motion.span
              aria-hidden
              className="pointer-events-none absolute -inset-1 rounded-[6px] border-2 border-accent"
              initial={{ opacity: 0.7, scale: 1 }}
              animate={{ opacity: 0, scale: 1.08 }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
            />
          ) : null}
          {content}
        </button>
      ) : (
        <div
          role="img"
          aria-label={cellLabel(cell, undefined)}
          className={cn(shell, 'bg-surface')}
          style={{ boxShadow: CELL_SHADOW }}
        >
          {content}
        </div>
      )}
    </div>
  );
};
